import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtErr, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge, PriorityBadge } from "@/components/badges";
import { ConfirmDialog, ActivationModePicker, copyText } from "@/components/common";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Send, Ban, ExternalLink, Copy, Pencil, Archive, ArchiveRestore, Trash2 } from "lucide-react";

const PORTAL_LABELS = { non_cree: "Non créé", en_attente: "En attente", actif: "Actif", desactive: "Désactivé" };  // eslint-disable-line no-unused-vars

export default function ClientDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const [client, setClient] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessMode, setAccessMode] = useState("invite");
  const [accessPwd, setAccessPwd] = useState("");
  const [accessConfirm, setAccessConfirm] = useState("");
  const [accessForm, setAccessForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [deletePuTarget, setDeletePuTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const canManage = ["super_admin", "company_admin"].includes(user?.role);

  const load = () => api.get(`/clients/${id}`).then(({ data }) => setClient(data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, [id]);

  const openEdit = () => {
    setEditForm({
      name: client.name || "", contact_name: client.contact_name || "",
      contact_email: client.contact_email || "", contact_phone: client.contact_phone || "",
      address: client.address || "", notes: client.notes || "",
    });
    setEditOpen(true);
  };

  const openAccess = () => {
    setAccessForm({ first_name: "", last_name: "", email: "", phone: "" });
    setAccessMode("invite"); setAccessPwd(""); setAccessConfirm("");
    setAccessOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/clients/${id}`, Object.fromEntries(Object.entries(editForm).map(([k, v]) => [k, v || null])));
      toast.success("Client mis à jour");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const doArchive = async () => {
    try {
      if (client.archived) {
        await api.post(`/clients/${id}/restore`);
        toast.success("Client restauré");
      } else {
        await api.post(`/clients/${id}/archive`);
        toast.success("Client archivé — accès portail désactivé");
      }
      load();
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };

  const createPortalUser = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/clients/${id}/portal-users`, {
        first_name: accessForm.first_name || null, last_name: accessForm.last_name || null,
        email: accessForm.email, phone: accessForm.phone || null,
        activation_mode: accessMode,
        password: accessMode === "manual" ? accessPwd : null,
        password_confirm: accessMode === "manual" ? accessConfirm : null,
      });
      if (data?.activation_link) setCredentials({ email: data.email, link: data.activation_link, emailStatus: data.email_status });
      const suffix = data?.email_status === "envoye" ? " — email envoyé via Brevo"
        : data?.email_status === "erreur" ? " — échec d'envoi email (lien de secours affiché)"
        : data?.email_status === "simule_envoye" ? " — email simulé" : "";
      toast.success((accessMode === "invite" ? "Utilisateur portail créé — invitation envoyée" : "Utilisateur portail créé et actif") + suffix);
      setAccessOpen(false);
      setAccessMode("invite"); setAccessPwd(""); setAccessConfirm("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  const portalUserAction = async (puId, kind) => {
    try {
      if (kind === "resend") {
        const { data } = await api.post(`/clients/${id}/portal-users/${puId}/resend`);
        if (data?.activation_link) setCredentials({ email: data.email, link: data.activation_link, emailStatus: data.email_status });
        toast.success("Invitation renvoyée");
      } else if (kind === "toggle") {
        await api.post(`/clients/${id}/portal-users/${puId}/toggle`);
        toast.success("Statut mis à jour");
      } else if (kind === "delete") {
        await api.delete(`/clients/${id}/portal-users/${puId}`);
        toast.success("Utilisateur portail supprimé");
      }
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (!client) return <div className="p-8 text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div data-testid="client-detail-page">
      <Link to="/app/clients" data-testid="back-to-clients" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Clients entreprise
      </Link>
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            {client.name}
            {client.archived && <span className="ml-3 rounded-sm bg-neutral-200 px-2 py-0.5 align-middle text-xs font-semibold text-neutral-500 dark:bg-neutral-800" data-testid="client-archived-badge">Archivé</span>}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">Société propriétaire : {client.company_name} · Créé le {fmtDate(client.created_at)}</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="edit-client-button" variant="outline" onClick={openEdit} className="rounded-sm">
            <Pencil className="mr-2 h-4 w-4" /> Modifier
          </Button>
          {canManage && (
            <Button data-testid="archive-client-button" variant="outline" onClick={() => setArchiveOpen(true)}
              className={client.archived ? "rounded-sm" : "rounded-sm border-primary text-primary hover:bg-primary hover:text-white"}>
              {client.archived ? <><ArchiveRestore className="mr-2 h-4 w-4" /> Restaurer</> : <><Archive className="mr-2 h-4 w-4" /> Archiver</>}
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-sm border border-border bg-card p-6">
          <h2 className="mb-5 text-lg font-medium">Fiche client</h2>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Contact principal</dt><dd className="mt-1 font-medium">{client.contact_name || "—"}</dd></div>
            <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Email</dt><dd className="mt-1 font-medium">{client.contact_email || "—"}</dd></div>
            <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Téléphone</dt><dd className="mt-1 font-medium">{client.contact_phone || "—"}</dd></div>
            <div><dt className="text-xs uppercase tracking-wider text-muted-foreground">Adresse</dt><dd className="mt-1 font-medium">{client.address || "—"}</dd></div>
          </dl>
          {client.notes && (
            <div className="mt-5 rounded-sm border border-[hsl(var(--note-border))] bg-[hsl(var(--note-bg))] p-4 text-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-primary">Notes internes</div>
              {client.notes}
            </div>
          )}
        </div>

        <div className="rounded-sm border border-border bg-card p-6" data-testid="portal-access-section">
          <div className="mb-5 flex items-center justify-between gap-3">
            <h2 className="text-lg font-medium">Utilisateurs portail client</h2>
            {canManage && !client.archived && (
              <Button data-testid="add-portal-user-button" size="sm" onClick={openAccess} className="rounded-sm bg-primary text-white hover:bg-red-700">
                <KeyRound className="mr-2 h-4 w-4" /> Ajouter un utilisateur portail
              </Button>
            )}
          </div>
          <div className="mb-4 flex items-center gap-2 rounded-sm border border-border bg-muted px-4 py-3">
            <code className="min-w-0 flex-1 truncate text-xs font-semibold text-primary">{client.portal_url}</code>
            <button data-testid="copy-client-portal-url" className="text-muted-foreground hover:text-primary" title="Copier"
              onClick={() => copyText(client.portal_url)}>
              <Copy className="h-3.5 w-3.5" />
            </button>
            <a data-testid="open-client-portal" href={client.portal_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary" title="Ouvrir">
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </div>
          <div className="space-y-2" data-testid="portal-users-list">
            {(client.portal_users || []).map((pu) => (
              <div key={pu.id} data-testid={`portal-user-row-${pu.email}`} className="flex flex-wrap items-center gap-3 rounded-sm border border-border p-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{[pu.first_name, pu.last_name].filter(Boolean).join(" ") || "—"}</div>
                  <div className="truncate text-xs text-muted-foreground">{pu.email}{pu.phone ? ` · ${pu.phone}` : ""}</div>
                </div>
                <span className={`whitespace-nowrap rounded-sm px-2 py-0.5 text-xs font-semibold ${pu.status === "active" ? "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300" : pu.status === "pending" ? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300" : "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300"}`}>
                  {pu.status === "active" ? "Actif" : pu.status === "pending" ? "En attente" : "Désactivé"}
                </span>
                {canManage && !client.archived && (
                  <div className="flex gap-1">
                    {pu.status === "pending" && (
                      <Button data-testid={`portal-user-resend-${pu.email}`} size="sm" variant="outline" className="rounded-sm" title="Renvoyer l'invitation" onClick={() => portalUserAction(pu.id, "resend")}>
                        <Send className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button data-testid={`portal-user-toggle-${pu.email}`} size="sm" variant="outline" className="rounded-sm" title={pu.status === "active" ? "Désactiver" : "Réactiver"} onClick={() => portalUserAction(pu.id, "toggle")}>
                      <Ban className="h-3.5 w-3.5" />
                    </Button>
                    <Button data-testid={`portal-user-delete-${pu.email}`} size="sm" variant="outline" className="rounded-sm border-primary text-primary hover:bg-primary hover:text-white" title="Supprimer" onClick={() => setDeletePuTarget(pu)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
            {!(client.portal_users || []).length && (
              <p className="rounded-sm border border-dashed border-border p-4 text-center text-xs text-muted-foreground" data-testid="portal-users-empty">
                Aucun utilisateur portail. Cliquez sur « Ajouter un utilisateur portail » pour créer un accès.
              </p>
            )}
          </div>
          {credentials && (
            <div data-testid="portal-credentials-box" className="mt-5 overflow-hidden rounded-sm border border-border bg-muted p-4 text-sm">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Lien d'activation (valide 72 h) — secours uniquement</div>
              <div>{credentials.email}</div>
              <div className="mt-2 flex items-center gap-2">
                <code data-testid="portal-activation-link" className="min-w-0 flex-1 truncate rounded-sm bg-card px-2 py-1 text-xs">{credentials.link}</code>
                <Button size="sm" variant="outline" data-testid="copy-portal-activation-link" className="shrink-0 rounded-sm"
                  onClick={() => copyText(credentials.link)}>Copier</Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground" data-testid="portal-invitation-email-note">
                {credentials.emailStatus === "envoye" ? "Email d'invitation envoyé via Brevo — le lien ci-dessus n'est qu'un secours."
                  : credentials.emailStatus === "erreur" ? "Échec d'envoi de l'email — transmettez le lien ci-dessus ou renvoyez l'invitation."
                  : "Email d'invitation simulé (aucun envoi réel) — transmettez le lien ci-dessus au client."}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 rounded-sm border border-border bg-card" data-testid="client-tickets-section">
        <div className="border-b border-border px-6 py-4">
          <h2 className="text-lg font-medium">Tickets associés ({client.tickets?.length || 0})</h2>
        </div>
        {(client.tickets || []).map((t) => (
          <Link key={t.id} to={`/app/tickets/${t.id}`} data-testid={`client-ticket-${t.number}`}
            className="flex flex-wrap items-center gap-3 border-b border-border px-6 py-4 last:border-b-0 hover:bg-accent transition-colors duration-200">
            <span className="font-mono text-xs text-muted-foreground">{t.number}</span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium">{t.subject}</span>
            {t.category_name && (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ background: t.category_color || "#666" }} />{t.category_name}
              </span>
            )}
            <StatusBadge status={t.status} />
            <PriorityBadge priority={t.priority} />
            <span className="text-xs text-muted-foreground">{fmtDateTime(t.created_at)}</span>
          </Link>
        ))}
        {!(client.tickets || []).length && <div className="p-8 text-center text-sm text-muted-foreground">Aucun ticket pour ce client.</div>}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-sm" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle>Modifier le client entreprise</DialogTitle>
          </DialogHeader>
          {editForm && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom du client</Label>
                <Input data-testid="edit-client-name" required value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact principal</Label>
                  <Input data-testid="edit-client-contact-name" value={editForm.contact_name} onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })} className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Email du contact</Label>
                  <Input data-testid="edit-client-contact-email" type="email" value={editForm.contact_email} onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input data-testid="edit-client-contact-phone" value={editForm.contact_phone} onChange={(e) => setEditForm({ ...editForm, contact_phone: e.target.value })} className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input data-testid="edit-client-address" value={editForm.address} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes internes</Label>
                <Input data-testid="edit-client-notes" value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="rounded-sm" />
              </div>
              <DialogFooter>
                <Button data-testid="edit-client-submit" type="submit" disabled={saving} className="rounded-sm bg-primary text-white hover:bg-red-700">
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={accessOpen} onOpenChange={setAccessOpen}>
        <DialogContent className="rounded-sm" data-testid="create-access-dialog">
          <DialogHeader>
            <DialogTitle>Nouvel utilisateur portail — {client.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Cet utilisateur pourra se connecter au portail de <b>{client.company_name}</b> pour créer et suivre les demandes de <b>{client.name}</b>.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input data-testid="portal-user-first-name" value={accessForm.first_name} onChange={(e) => setAccessForm({ ...accessForm, first_name: e.target.value })} className="rounded-sm" placeholder="Ex : Claire" />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input data-testid="portal-user-last-name" value={accessForm.last_name} onChange={(e) => setAccessForm({ ...accessForm, last_name: e.target.value })} className="rounded-sm" placeholder="Ex : Martin" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input data-testid="portal-user-email" type="email" required value={accessForm.email} onChange={(e) => setAccessForm({ ...accessForm, email: e.target.value })} className="rounded-sm" placeholder="prenom@client.fr" />
              </div>
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input data-testid="portal-user-phone" value={accessForm.phone} onChange={(e) => setAccessForm({ ...accessForm, phone: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <ActivationModePicker testid="portal-activation"
              inviteText="Un email d'invitation sera envoyé avec un lien d'activation valide 72 h (le lien reste affiché en secours)."
              mode={accessMode} onModeChange={setAccessMode}
              password={accessPwd} onPasswordChange={setAccessPwd}
              confirm={accessConfirm} onConfirmChange={setAccessConfirm} />
            <DialogFooter>
              <Button data-testid="create-access-submit"
                disabled={saving || !accessForm.email || (accessMode === "manual" && (accessPwd.length < 8 || accessPwd !== accessConfirm))}
                onClick={createPortalUser}
                className="rounded-sm bg-primary text-white hover:bg-red-700">
                {saving ? "Création…" : "Créer l'utilisateur portail"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={archiveOpen} onOpenChange={setArchiveOpen}
        title={client.archived ? `Restaurer ${client.name} ?` : `Archiver ${client.name} ?`}
        description={client.archived
          ? "Le client réapparaîtra dans la liste. L'accès portail restera désactivé (réactivez-le si besoin)."
          : "Êtes-vous sûr de vouloir archiver ce client entreprise ? Son accès portail sera désactivé et il sera masqué des listes. Les données et tickets restent conservés. Action journalisée."}
        confirmLabel={client.archived ? "Restaurer" : "Archiver"}
        onConfirm={doArchive} testid="archive-client-dialog" />
      <ConfirmDialog open={!!deletePuTarget} onOpenChange={() => setDeletePuTarget(null)}
        title={`Supprimer ${deletePuTarget ? [deletePuTarget.first_name, deletePuTarget.last_name].filter(Boolean).join(" ") || deletePuTarget.email : ""} ?`}
        description="Cet utilisateur portail sera définitivement supprimé et ne pourra plus se connecter. Ses tickets déjà créés sont conservés. Action journalisée."
        confirmLabel="Supprimer"
        onConfirm={async () => { const t = deletePuTarget; setDeletePuTarget(null); await portalUserAction(t.id, "delete"); }}
        testid="delete-portal-user-dialog" />
    </div>
  );
}
